import { loginUrlFor } from "@/auth/login-url";
import { bootstrapAuth } from "@/auth/me";
import { msSinceServerContact } from "@/lib/server-contact";
import { useAuthStore, type SessionEndedReason } from "@/stores/auth-store";

// Proactive session-expiry detection (ticket #67).
//
// Without this the SPA keeps rendering a logged-in UI after the session dies:
// the only expiry it knows about is `accessTokenExpiresAt`, which the BFF
// silently refreshes, so it is useless as a logout signal. Expiry surfaces only
// when an operation fails.
//
// The rule here is: ask the server when the user re-engages after a gap in
// server contact. Three properties matter.
//
//   1. The client never decides. It decides only whether to ASK. A cached
//      deadline is wrong in both directions - it slides outward on every silent
//      refresh, and revocation or a BFF restart can kill a session early - so
//      only the server's answer is trusted.
//   2. The gate is server-contact age, not input-idleness. See lib/server-contact.
//   3. Inconclusive means alive. Only an explicit "not authenticated" logs the
//      user out; a network failure leaves them working.
//
// Not covered: ssoSessionMaxLifespan (10h). A continuously active user never
// opens the gate, so that ceiling is still discovered reactively. It needs a
// scheduled warning, which is deliberately a separate feature.
//
// One verdict is not final. When the BFF reports "tokens-lost" - the tokens went
// missing BEFORE their deadline, which is what a BFF restart or a Valkey flush
// looks like - no time limit was reached and Keycloak probably still knows this
// user. Telling them their session ended would be wrong twice: wrong about the
// cause, and wrong that they have to do anything about it. So we try to recover
// silently (prompt=none) first and only raise the notice if that is declined.
// Once per install, because a recovery that lands us back here would otherwise
// loop.

const PROBE_AFTER_MS = 60_000;
const MIN_PROBE_INTERVAL_MS = 30_000;

const SIGNALS = ["focus", "scroll", "keydown", "pointerdown"] as const;

export interface SessionWatchOptions {
  probeAfterMs?: number;
  minProbeIntervalMs?: number;
  /** Defaults to raising the notice; injectable for tests. */
  onExpired?: (reason?: SessionEndedReason) => void;
  /** Defaults to a prompt=none round trip; injectable for tests. */
  onRecoverable?: () => void;
}

export function installSessionWatch(options: SessionWatchOptions = {}): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};

  const probeAfterMs = options.probeAfterMs ?? PROBE_AFTER_MS;
  const minProbeIntervalMs = options.minProbeIntervalMs ?? MIN_PROBE_INTERVAL_MS;
  const onExpired =
    options.onExpired ?? ((reason?: SessionEndedReason) => useAuthStore.getState().markSessionExpired(reason));
  const onRecoverable =
    options.onRecoverable ??
    (() => {
      window.location.assign(
        loginUrlFor(window.location.pathname + window.location.search, { silent: true }),
      );
    });

  let lastProbeAt = 0;
  let inFlight = false;
  let disposed = false;
  let recoveryAttempted = false;

  async function probe(): Promise<void> {
    inFlight = true;
    try {
      const me = await bootstrapAuth();
      if (disposed) return;
      if (useAuthStore.getState().status === "authenticated") return;
      if (me.sessionEnded === "tokens-lost" && !recoveryAttempted) {
        recoveryAttempted = true;
        onRecoverable();
        return;
      }
      onExpired(me.sessionEnded);
    } catch {
      // Unreachable BFF, 5xx, malformed body: we did not learn anything, so the
      // user keeps their session. Logging out on an inconclusive answer would be
      // worse than the bug this fixes.
    } finally {
      inFlight = false;
    }
  }

  function maybeProbe(): void {
    if (disposed || inFlight) return;
    // A signal from a hidden tab is not re-engagement; visibilitychange handles
    // the return itself.
    if (document.visibilityState !== "visible") return;
    if (useAuthStore.getState().status !== "authenticated") return;
    if (msSinceServerContact() < probeAfterMs) return;
    const now = Date.now();
    if (now - lastProbeAt < minProbeIntervalMs) return;
    lastProbeAt = now;
    void probe();
  }

  const onVisibility = (): void => {
    if (document.visibilityState === "visible") maybeProbe();
  };
  // A bfcache restore can reinstate a page hours later without a
  // visibilitychange, so it needs its own signal.
  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) maybeProbe();
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pageshow", onPageShow);
  for (const signal of SIGNALS) {
    window.addEventListener(signal, maybeProbe, { passive: true });
  }

  return () => {
    disposed = true;
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pageshow", onPageShow);
    for (const signal of SIGNALS) {
      window.removeEventListener(signal, maybeProbe);
    }
  };
}
